import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError, api, streamChat } from '@shared/api';
import { ModelLogo, providerName } from '@shared/brand';
import { compact, idr, ms, num } from '@shared/format';
import { refreshBalance } from '@shared/hooks';
import { friendlyError } from '../errors';
import { LANGS, type Lang } from '../snippets';
import { Alert, Card, CodeBlock, CopyButton, Icon, InfoTip, Loading, PageHeader, Segmented, Select, Tabs } from '../components/ui';
import { ModelPicker } from '../components/ModelPicker';
import { modalityOf, providerOf, type CatalogModel, type Modality } from '../catalog';
import { chatRequestSnippet } from './playground/request-code';
import { ImagePlayground, VideoPlayground } from './playground/MediaPlayground';
import '../styles/playground.css';

type Mode = 'chat' | 'image' | 'video';

const MODES: ReadonlyArray<{ id: Mode; label: ReactNode }> = [
  { id: 'chat', label: <><Icon name="chat" size={16} />Chat</> },
  { id: 'image', label: <><Icon name="image" size={16} />Gambar</> },
  { id: 'video', label: <><Icon name="video" size={16} />Video</> },
];

const MODE_OF: Record<Exclude<Modality, 'embedding'>, Mode> = { text: 'chat', image: 'image', video: 'video' };
const PREFERRED: Record<Mode, string> = { chat: 'claude-sonnet-5-5', image: 'seedream-5.0-flash', video: 'seedance-2.0-fast' };

interface Project {
  id: string;
  name: string;
  environment: string;
}

const PATH = '/v1/playground/chat/completions';

const ENV_LABEL: Record<string, string> = { DEVELOPMENT: 'Development', STAGING: 'Staging', PRODUCTION: 'Production' };

const SUGGESTIONS = [
  'Jelaskan apa itu Kubernetes dalam tiga kalimat.',
  'Tulis fungsi Python untuk mengecek format NIK 16 digit.',
  'Ringkas email ini jadi tiga poin: "Rapat dipindah ke Kamis jam 10, mohon siapkan laporan Q3."',
];

function asMode(value: string | null): Mode | null {
  return value === 'chat' || value === 'image' || value === 'video' ? value : null;
}

export function Playground() {
  const [params, setParams] = useSearchParams();
  const [catalog, setCatalog] = useState<CatalogModel[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState('');
  const [picked, setPicked] = useState<Record<Mode, string>>({ chat: '', image: '', video: '' });
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState<number | null>(null);

  useEffect(() => {
    const loadBalance = () =>
      api<{ available_balance: number }>('/v1/wallet/balance')
        .then((body) => setBalance(body.available_balance))
        .catch(() => setBalance(null));
    void loadBalance();
    window.addEventListener('aiagg:balance', loadBalance);
    return () => window.removeEventListener('aiagg:balance', loadBalance);
  }, []);

  useEffect(() => {
    Promise.all([api<{ data: CatalogModel[] }>('/v1/models'), api<{ data: Project[] }>('/v1/projects')])
      .then(([models, projectList]) => {
        // embeddings: no playground mode
        setCatalog(models.data.filter((m) => modalityOf(m) !== 'embedding'));
        setProjects(projectList.data);
        setProjectId((current) => current || projectList.data[0]?.id || '');
      })
      .catch((err: Error) => setLoadError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const byMode = useMemo(() => {
    const groups: Record<Mode, CatalogModel[]> = { chat: [], image: [], video: [] };
    for (const m of catalog) groups[MODE_OF[modalityOf(m) as Exclude<Modality, 'embedding'>]].push(m);
    return groups;
  }, [catalog]);

  const urlModel = params.get('model');
  const urlModelEntry = catalog.find((m) => m.id === urlModel);
  const mode: Mode = asMode(params.get('mode')) ?? (urlModelEntry ? MODE_OF[modalityOf(urlModelEntry) as Exclude<Modality, 'embedding'>] : 'chat');

  // defaults, then URL wins
  useEffect(() => {
    if (catalog.length === 0) return;
    setPicked((current) => {
      const next = { ...current };
      (Object.keys(next) as Mode[]).forEach((key) => {
        const list = byMode[key];
        if (!list.some((m) => m.id === next[key])) {
          next[key] = (list.find((m) => m.id === PREFERRED[key]) ?? list.find((m) => providerOf(m) !== 'mock') ?? list[0])?.id ?? '';
        }
      });
      if (urlModelEntry) next[MODE_OF[modalityOf(urlModelEntry) as Exclude<Modality, 'embedding'>]] = urlModelEntry.id;
      return next;
    });
  }, [catalog, byMode, urlModelEntry]);

  const setMode = (next: Mode) => {
    const model = picked[next];
    setParams(model ? { mode: next, model } : { mode: next }, { replace: true });
  };

  const pickModel = (target: Mode, id: string) => {
    setPicked((current) => ({ ...current, [target]: id }));
    setParams({ mode: target, model: id }, { replace: true });
  };

  const projectTip = (
    <InfoTip label="Tentang proyek">
      Biaya dari playground dicatat ke proyek ini, tampil di statistiknya, dan ikut dihitung dalam batas biaya proyek.
    </InfoTip>
  );

  const projectField =
    projects.length > 1 ? (
      <div className="field">
        <span className="label label-tip">
          <label htmlFor="pg-project">Tagihkan ke proyek</label>
          {projectTip}
        </span>
        <Select
          id="pg-project"
          value={projectId}
          onChange={setProjectId}
          options={projects.map((p) => ({
            value: p.id,
            label: p.name,
            description: ENV_LABEL[p.environment] ?? p.environment,
            icon: <Icon name="folder" size={15} />,
          }))}
        />
      </div>
    ) : projects.length === 1 ? (
      <div className="pg-project">
        <Icon name="folder" size={16} />
        <span className="grow">
          <span className="pg-project-label label-tip">
            Proyek
            {projectTip}
          </span>
          <span className="pg-project-name">{projects[0].name}</span>
        </span>
        <Link to={`/projects/${projects[0].id}`} className="small">
          Statistik
        </Link>
      </div>
    ) : null;

  return (
    <div className="pg">
      <PageHeader
        title="Playground"
        subtitle="Coba model langsung dari browser tanpa API key. Biaya dipotong dari saldo seperti request biasa."
        actions={<Segmented label="Mode" options={MODES} value={mode} onChange={setMode} />}
      />

      {loadError && <Alert kind="error">{loadError}</Alert>}
      {balance !== null && balance <= 0 && (
        <Alert kind="warn">
          Saldo Anda {idr(balance)}, jadi request akan ditolak. <Link to="/wallet">Isi saldo</Link> dulu untuk mencoba model.
        </Alert>
      )}
      {loading ? (
        <Loading label="Memuat model" />
      ) : mode === 'image' ? (
        <ImagePlayground
          models={byMode.image}
          model={picked.image}
          onModelChange={(id) => pickModel('image', id)}
          projectId={projectId}
          projectField={projectField}
        />
      ) : mode === 'video' ? (
        <VideoPlayground
          models={byMode.video}
          model={picked.video}
          onModelChange={(id) => pickModel('video', id)}
          projectId={projectId}
          projectField={projectField}
        />
      ) : (
        <ChatPlayground
          models={byMode.chat}
          model={picked.chat}
          onModelChange={(id) => pickModel('chat', id)}
          projectId={projectId}
          projectField={projectField}
        />
      )}
    </div>
  );
}

interface RunStats {
  id?: string;
  model?: string;
  provider?: string;
  promptTokens?: number;
  completionTokens?: number;
  cachedTokens?: number;
  cost?: number;
  latency?: number;
  ttft?: number;
}

interface Turn {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  model?: string;
  streaming?: boolean;
  stopped?: boolean;
  failed?: boolean;
  error?: ReactNode;
  stats?: RunStats;
}

interface MetaLike {
  id?: string;
  model?: string;
  provider?: string;
  usage?: unknown;
  billing?: unknown;
}

function mergeMeta(stats: RunStats, meta: MetaLike): RunStats {
  const usage = meta.usage as { prompt_tokens?: number; completion_tokens?: number; prompt_tokens_details?: { cached_tokens?: number } } | undefined;
  const billing = meta.billing as { amount?: number } | undefined;
  return {
    ...stats,
    id: meta.id ?? stats.id,
    model: meta.model ?? stats.model,
    provider: meta.provider ?? stats.provider,
    promptTokens: usage?.prompt_tokens ?? stats.promptTokens,
    completionTokens: usage?.completion_tokens ?? stats.completionTokens,
    cachedTokens: usage?.prompt_tokens_details?.cached_tokens ?? stats.cachedTokens,
    cost: billing?.amount ?? stats.cost,
  };
}

function statsParts(stats: RunStats): string[] {
  const tokens =
    stats.promptTokens !== undefined || stats.completionTokens !== undefined
      ? `${num(stats.promptTokens ?? 0)} → ${num(stats.completionTokens ?? 0)} token`
      : null;
  return [tokens, stats.cost !== undefined ? idr(stats.cost, true) : null, stats.latency !== undefined ? ms(stats.latency) : null].filter(
    (part): part is string => part !== null,
  );
}

let turnSeq = 0;

function ChatPlayground({
  models,
  model,
  onModelChange,
  projectId,
  projectField,
}: {
  models: CatalogModel[];
  model: string;
  onModelChange: (id: string) => void;
  projectId: string;
  projectField: ReactNode;
}) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('Kamu asisten yang membantu. Jawab dalam Bahasa Indonesia.');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(1000);
  const [streaming, setStreaming] = useState(true);
  const [running, setRunning] = useState(false);
  const [last, setLast] = useState<RunStats | null>(null);
  const [lang, setLang] = useState<Lang>('curl');
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const selected = models.find((m) => m.id === model);
  const sampling = selected?.capabilities.sampling_params !== false;
  const sessionCost = turns.reduce((sum, t) => sum + (t.stats?.cost ?? 0), 0);
  const lastPrompt = [...turns].reverse().find((t) => t.role === 'user')?.content;
  const nameOf = (id?: string) => models.find((m) => m.id === id)?.display_name ?? id ?? 'Model';

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const send = async () => {
    const text = input.trim();
    if (!text || running || !model) return;

    const history = turns.filter((t) => !t.failed && t.content).map((t) => ({ role: t.role, content: t.content }));
    const body = {
      model,
      messages: [...(systemPrompt.trim() ? [{ role: 'system', content: systemPrompt }] : []), ...history, { role: 'user', content: text }],
      max_tokens: maxTokens,
      ...(sampling ? { temperature } : {}),
      project_id: projectId || undefined,
    };

    const userId = ++turnSeq;
    const replyId = ++turnSeq;
    setTurns((current) => [
      ...current,
      { id: userId, role: 'user', content: text },
      { id: replyId, role: 'assistant', content: '', model, streaming: true },
    ]);
    setInput('');
    setRunning(true);

    const controller = new AbortController();
    abortRef.current = controller;
    const started = performance.now();
    let firstToken: number | undefined;
    let stats: RunStats = { model };
    let failure: ReactNode = null;
    const patchReply = (fn: (turn: Turn) => Turn) => setTurns((current) => current.map((t) => (t.id === replyId ? fn(t) : t)));

    try {
      if (streaming) {
        await streamChat({
          path: PATH,
          body,
          signal: controller.signal,
          onChunk: (delta) => {
            if (firstToken === undefined) firstToken = performance.now() - started;
            patchReply((t) => ({ ...t, content: t.content + delta }));
          },
          onMeta: (meta) => {
            stats = mergeMeta(stats, meta);
          },
          onError: (e) => {
            failure = friendlyError(e.type, e.message, e.request_id);
          },
        });
      } else {
        const result = await api<MetaLike & { choices?: Array<{ message?: { content?: string } }> }>(PATH, {
          method: 'POST',
          body,
          signal: controller.signal,
        });
        patchReply((t) => ({ ...t, content: result.choices?.[0]?.message?.content ?? '' }));
        stats = mergeMeta(stats, result);
      }
    } catch (err) {
      // abort isn't an error
      if (!controller.signal.aborted) {
        failure = err instanceof ApiError ? friendlyError(err.type, err.message, err.requestId) : (err as Error).message;
      }
    } finally {
      stats = { ...stats, latency: performance.now() - started, ttft: firstToken };
      const stopped = controller.signal.aborted;
      const failed = failure !== null;
      setTurns((current) =>
        current.map((t) =>
          t.id === replyId
            ? { ...t, streaming: false, stopped, failed, error: failure ?? undefined, stats }
            : t.id === userId && failed
              ? { ...t, failed: true }
              : t,
        ),
      );
      setLast(stats);
      setRunning(false);
      abortRef.current = null;
      refreshBalance();
      inputRef.current?.focus();
    }
  };

  const stop = () => abortRef.current?.abort();

  const onKey = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void send();
    }
  };

  const clear = () => {
    abortRef.current?.abort();
    setTurns([]);
    setLast(null);
  };

  if (models.length === 0) return <Alert kind="info">Belum ada model teks yang aktif.</Alert>;

  const code = chatRequestSnippet(lang, {
    model: model || 'claude-sonnet-5-5',
    system: systemPrompt,
    prompt: input.trim() || lastPrompt || SUGGESTIONS[0],
    temperature: sampling ? temperature : undefined,
    maxTokens,
    stream: streaming,
  });
  const userCount = turns.filter((t) => t.role === 'user').length;

  return (
    <div className="split">
      <div className="card pg-chat">
        <div className="pg-toolbar">
          <div className="pg-picker">
            <ModelPicker models={models} value={model} onChange={onModelChange} disabled={running} />
          </div>
          <span className="grow" />
          {userCount > 0 && (
            <span className="pg-session">
              {userCount} pesan · {idr(sessionCost, true)}
            </span>
          )}
          <button className="btn btn-sm btn-ghost" onClick={clear} disabled={turns.length === 0}>
            <Icon name="plus" size={15} />
            Percakapan baru
          </button>
        </div>

        <div className="pg-thread" ref={scrollRef} aria-live="polite">
          {turns.length === 0 ? (
            <div className="pg-intro">
              {selected && <ModelLogo model={selected.id} family={selected.family} provider={providerOf(selected)} size={48} />}
              <h2>{selected ? selected.display_name : 'Pilih model'}</h2>
              {selected && (
                <p className="pg-intro-meta">
                  {providerName(providerOf(selected))} · konteks {compact(selected.context_window)} token
                  {selected.pricing && (
                    <>
                      {' '}
                      · {idr(selected.pricing.input_per_1m)} input, {idr(selected.pricing.output_per_1m)} output per 1 jt token
                    </>
                  )}
                </p>
              )}
              <div className="pg-suggest">
                {SUGGESTIONS.map((text) => (
                  <button
                    key={text}
                    type="button"
                    onClick={() => {
                      setInput(text);
                      inputRef.current?.focus();
                    }}
                  >
                    {text}
                  </button>
                ))}
              </div>
              <p className="pg-intro-note">Riwayat percakapan ikut terkirim di setiap pesan, jadi token input bertambah seiring percakapan.</p>
            </div>
          ) : (
            turns.map((turn) =>
              turn.role === 'user' ? (
                <div key={turn.id} className={turn.failed ? 'msg msg-user failed' : 'msg msg-user'}>
                  <div className="msg-bubble">{turn.content}</div>
                  {turn.failed && <div className="msg-note">Tidak terkirim</div>}
                </div>
              ) : (
                <div key={turn.id} className="msg msg-ai">
                  <ModelLogo model={turn.stats?.model ?? turn.model} size={30} />
                  <div className="msg-body">
                    <div className="msg-head">
                      <span className="msg-name">{nameOf(turn.model)}</span>
                      {turn.streaming && <span className="spinner" />}
                    </div>
                    {turn.content ? (
                      <div className="msg-text">{turn.content}</div>
                    ) : turn.streaming ? (
                      <div className="msg-wait">Menunggu respons…</div>
                    ) : null}
                    {turn.error && (
                      <div className="msg-error" role="alert">
                        <Icon name="error" size={15} />
                        <div>{turn.error}</div>
                      </div>
                    )}
                    {turn.stopped && <div className="msg-note">Dihentikan. Token yang sudah keluar tetap ditagih.</div>}
                    {!turn.streaming && !turn.error && turn.stats && (
                      <div className="msg-meta" title={turn.stats.ttft !== undefined ? `Token pertama ${ms(turn.stats.ttft)}` : undefined}>
                        {statsParts(turn.stats).map((part) => (
                          <span key={part}>{part}</span>
                        ))}
                        {turn.content && <CopyButton value={turn.content} className="btn-ghost msg-copy" />}
                      </div>
                    )}
                  </div>
                </div>
              ),
            )
          )}
        </div>

        <div className="pg-composer">
          <textarea
            ref={inputRef}
            rows={2}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            placeholder={selected ? `Kirim pesan ke ${selected.display_name}` : 'Pilih model dulu'}
            aria-label="Pesan"
          />
          <div className="pg-composer-bar">
            <span className="pg-hint">
              <kbd>Enter</kbd> kirim · <kbd>Shift</kbd> + <kbd>Enter</kbd> baris baru
            </span>
            <span className="grow" />
            {running ? (
              <button className="btn btn-sm btn-danger" onClick={stop}>
                <Icon name="stop" size={14} />
                Stop
              </button>
            ) : (
              <button className="btn btn-sm btn-primary" onClick={() => void send()} disabled={!input.trim() || !model}>
                Kirim
                <Icon name="arrow" size={15} />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="col pg-rail">
        <Card title="Pengaturan">
          {projectField}
          <div className="field">
            <span className="label label-tip">
              <label htmlFor="pg-system">Instruksi sistem</label>
              <InfoTip label="Tentang instruksi sistem">
                Arahan tetap yang dikirim di awal setiap percakapan: peran, gaya bahasa, atau format jawaban. Ikut dihitung sebagai token
                masuk.
              </InfoTip>
            </span>
            <textarea id="pg-system" rows={3} value={systemPrompt} onChange={(e) => setSystemPrompt(e.target.value)} />
          </div>
          <div className="field">
            <div className="row between">
              <span className="label label-tip">
                <label htmlFor="pg-temp">Temperature</label>
                <InfoTip label="Tentang temperature" width={300}>
                  <p>Mengatur seberapa bervariasi jawaban.</p>
                  <ul>
                    <li>
                      <b>Rendah (0–0,3)</b>: konsisten dan faktual. Cocok untuk ekstraksi data, klasifikasi, atau kode.
                    </li>
                    <li>
                      <b>Tinggi (0,7–1)</b>: lebih beragam dan kreatif. Cocok untuk brainstorming atau menulis.
                    </li>
                  </ul>
                  <p>Sebagian model mengatur temperature sendiri dan mengabaikan nilai ini.</p>
                </InfoTip>
              </span>
              <span className="pg-value">{sampling ? temperature.toFixed(1) : 'Otomatis'}</span>
            </div>
            <input
              id="pg-temp"
              type="range"
              className="pg-range"
              min={0}
              max={2}
              step={0.1}
              value={temperature}
              disabled={!sampling}
              onChange={(e) => setTemperature(Number(e.target.value))}
            />
            {sampling ? (
              <div className="pg-range-scale" aria-hidden>
                <span>Konsisten</span>
                <span>Kreatif</span>
              </div>
            ) : (
              <span className="field-hint">Model ini mengatur temperature sendiri.</span>
            )}
          </div>
          <div className="field">
            <span className="label label-tip">
              <label htmlFor="pg-max">Maks. token output</label>
              <InfoTip label="Tentang maks. token output" width={300}>
                <p>Batas panjang jawaban, sekitar ¾ kata per token. Makin kecil, makin murah biaya maksimumnya.</p>
                <p>Model reasoning bisa memakai sebagian token untuk berpikir dulu, jadi beri ruang lebih bila jawabannya terpotong.</p>
              </InfoTip>
            </span>
            <input
              id="pg-max"
              type="number"
              min={1}
              max={selected?.max_output_tokens}
              value={maxTokens}
              onChange={(e) => setMaxTokens(Number(e.target.value))}
            />
          </div>
          <div className="pg-toggle-row">
            <span className="grow">
              <span className="pg-toggle-label label-tip">
                <label htmlFor="pg-stream">Streaming</label>
                <InfoTip label="Tentang streaming">
                  Jawaban dikirim sepotong demi sepotong sambil ditulis, jadi pengguna tidak menunggu sampai selesai. Biayanya sama.
                </InfoTip>
              </span>
              <span className="field-hint">Jawaban tampil sambil ditulis.</span>
            </span>
            <input id="pg-stream" type="checkbox" className="switch" checked={streaming} onChange={(e) => setStreaming(e.target.checked)} />
          </div>
        </Card>

        <Card title="Run terakhir">
          {last ? (
            <>
              <div className="pg-stats">
                <div>
                  <span>Token masuk</span>
                  <b>{last.promptTokens !== undefined ? num(last.promptTokens) : '—'}</b>
                  {last.cachedTokens ? <small>{num(last.cachedTokens)} dari cache</small> : null}
                </div>
                <div>
                  <span>Token keluar</span>
                  <b>{last.completionTokens !== undefined ? num(last.completionTokens) : '—'}</b>
                </div>
                <div>
                  <span>Biaya</span>
                  <b>{last.cost !== undefined ? idr(last.cost, true) : '—'}</b>
                </div>
                <div>
                  <span>Latensi</span>
                  <b>{ms(last.latency)}</b>
                  {last.ttft !== undefined && <small>Token pertama {ms(last.ttft)}</small>}
                </div>
              </div>
              {last.id && (
                <div className="pg-reqid">
                  <code className="grow truncate">{last.id}</code>
                  <CopyButton value={last.id} label="Salin ID" />
                </div>
              )}
            </>
          ) : (
            <p className="muted small" style={{ margin: 0 }}>
              Token, biaya, dan latensi dari pesan terakhir akan tampil di sini.
            </p>
          )}
        </Card>

        <div>
          <CodeBlock code={code} tabs={<Tabs tight tabs={LANGS} value={lang} onChange={setLang} />} />
          <p className="field-hint mt-sm">
            Request yang sama dengan API key Anda. Belum punya? <Link to="/api-keys?baru=1">Buat API key</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
